import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import {
  type ChartData,
  type ChartDataset,
  type ScatterDataPoint,
  type TimeUnit,
} from "chart.js";
import clsx from "clsx";
import copy from "copy-to-clipboard";
import { JsonEditor } from "jsoneditor-react";
import moment from "moment";
import { Bar, Line } from "react-chartjs-2";
import { useToasts } from "react-toast-notifications";
import { UAParser } from "ua-parser-js";
import { useQueryParam, withDefault } from "use-query-params";

import { LoadingSlideNoText } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  type Column,
  CustomPaging,
  DataTypeProvider,
  IntegratedFiltering,
  IntegratedPaging,
  PagingState,
  SearchState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { useFeatureFlags } from "@hooks/http";
import { useNorthbeamQuery } from "@hooks/http/use-northbeam-query";
import {
  DateRangeParam,
  DateRangeQueryParam,
  TimeGranularityParam,
  useDateRangeQueryState,
} from "@hooks/query-params";
import { usePrevious } from "@hooks/use-previous";
import {
  type FetchPixelTrackingEvents,
  type FetchPixelTrackingEventsVariables,
  type FetchPixelTrackingEvents_me_pixelTrackingEvents_summary,
  type FetchServerAPISummaryTrackingEvents,
  type FetchServerAPISummaryTrackingEventsVariables,
  type FetchServerAPISummaryTrackingEvents_me_serverApiTrackingEvents_summary,
  type FetchServerAPITrackingEvents,
  type FetchServerAPITrackingEventsVariables,
  ServerAPIServiceType,
  TimeGranularityEnum,
} from "@north-beam/nb-client-side";
import {
  CustomJsonParam,
  type ITimeGranularityV2,
  dateRangeOptionsForTimeGranularity,
  formatting,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { PixelValidation } from "@pages/onboarding/core/verify/pixel-validation";
import { LoadingPanel } from "@pages/sales/tab-table-section/table/plugins";
import {
  TableHeaderRowContent,
  TableHeaderRowSortLabel,
} from "@pages/sales/tab-table-section/table/table-components";
import { PrimaryButton } from "@shared/buttons";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";
import { Modal } from "@shared/modal";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { parseDateRangeArgument } from "@utils/index";

import {
  FETCH_PIXEL_TRACKING_EVENTS,
  FETCH_SERVER_API_SUMMARY,
  FETCH_SERVER_API_TRACKING_EVENTS,
} from "../queries";

import "./table.scss";

const DATE_RANGE_CHOICE_LABELS = {
  today: "Today",
  yesterday: "Yest",
  yesterdayAndToday: "Yest & today",
  todayAndYesterday: "Today & yest",
  last3Days: "3d",
  last7Days: "7d",
  last14Days: "14d",
  last30Days: "30d",
  monthToDate: "Mo to date",
  lastMonth: "Last mo",
  thisWeek: "This week",
  lastWeek: "Last week",
  thisWeekAndLastWeek: "This week & last week",
  last4Weeks: "1m",
  thisMonth: "This mo",
} as const;

export const TrackingEvents = () => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  return (
    <>
      {!user.isPixelConfigurationSubaccount && (
        <>
          <div className="text-4xl mt-5  border-b-3 pb-5 border-b-interstellar-1 mx-10">
            Pixel Validation
          </div>
          <div className="mt-5 px-10 pb-12">
            <PixelDiagnosticsChart />
            {hasFeatureFlag("enablePixelValidationInOnboardingFlow") && (
              <PixelValidation />
            )}
          </div>
        </>
      )}
      <div className="text-4xl mt-5  border-b-3 pb-5 border-b-interstellar-1 mx-10">
        Orders API Validation (only applicable if using the Orders API)
      </div>
      <div className="mt-5 px-10 pb-12">
        <ServerDiagnosticsDisplay spendApi={false} ordersApi={true} />
      </div>
      <div className="text-4xl mt-5   border-b-3 pb-5 border-b-interstellar-1 mx-10">
        Spend API Validation (only applicable if using the Spend API)
      </div>
      <div className="mt-5 px-10 pb-5">
        <ServerDiagnosticsDisplay spendApi={true} ordersApi={false} />
      </div>
    </>
  );
};

const usePixelDiagnosticsPageFilters = () => {
  const [dateRangeState, setDateRange] = useDateRangeQueryState({
    exclude: {
      compareDateRange: true,
    },
    queryParams: {
      [DateRangeQueryParam.TIME_GRANULARITY]: withDefault(
        TimeGranularityParam,
        "hourly" as const,
      ),

      [DateRangeQueryParam.DATE_RANGE]: withDefault(
        DateRangeParam,
        "today" as const,
      ),
    },
  });

  // filter options
  const dateRange = parseDateRangeArgument(
    dateRangeState.dateRange,
    todayLocalAsISODate(),
  );

  const filter = useMemo<FetchPixelTrackingEventsVariables>(() => {
    return {
      startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
      endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
      timeGranularity: timeGranularityEnumFromString(
        dateRangeState.timeGranularity ?? "",
      ),
    };
  }, [dateRange, dateRangeState.timeGranularity]);

  return [filter, setDateRange, dateRangeState] as const;
};

function timeGranularityEnumFromString(val: string): TimeGranularityEnum {
  switch (val) {
    case "hourly":
      return TimeGranularityEnum.HOURLY;
    case "daily":
      return TimeGranularityEnum.DAILY;
    case "weekly":
      return TimeGranularityEnum.WEEKLY;
  }
  return TimeGranularityEnum.DAILY;
}

const ChartControl = () => {
  const [, setDateRangeState, dateRangeState] =
    usePixelDiagnosticsPageFilters();
  const [selected, setSelected] = useState<FlatSelectOption<string>>({
    label: "",
    value: "",
  });

  useEffect(() => {
    setSelected({
      label: "",
      value: dateRangeState.dateRange as string,
    });
  }, [dateRangeState.dateRange]);

  return (
    <>
      <div className="py-4 flex justify-between items-end space-x-4">
        <TimeGranularityV2Select
          label="GRANULARITY"
          timeGranularity={dateRangeState.timeGranularity as ITimeGranularityV2}
          hiddenGranularities={["monthly", "weekly"]}
          onChange={(value: ITimeGranularityV2) => {
            setDateRangeState({
              timeGranularity: value,
            });
          }}
          width="92px"
          icon={null}
        />
        <div className="text-xs">
          <FlatSelect
            options={[
              ...dateRangeOptionsForTimeGranularity(
                dateRangeState.timeGranularity,
              )
                .filter(
                  (i) =>
                    !["last14Days", "lastMonth", "monthToDate"].includes(
                      i.value,
                    ),
                )
                .map((e) => {
                  return {
                    label: DATE_RANGE_CHOICE_LABELS[e.value],
                    value: e.value,
                    onClick: () => {
                      setDateRangeState({
                        dateRange: e.value,
                      });
                    },
                  };
                })
                .filter((item) => item.label),
            ]}
            selected={selected}
            setSelected={setSelected}
          />
        </div>
      </div>
    </>
  );
};
const SERVER_COLUMN_MAPPINGS = {
  request_id: { name: "request_id", title: "Request ID" },
  message: { name: "message", title: "Error message" },
  error_type: { name: "error_type", title: "Error type" },
  timestamp: { name: "timestamp", title: "Timestamp of error" },
  context: { name: "context", title: "Link to request" },
};

const SERVER_DEFAULT_COLUMN_WIDTHS = {
  request_id: { columnName: "request_id", width: 350 },
  message: { columnName: "message", width: 200 },
  error_type: { columnName: "error_type", width: 200 },
  timestamp: { columnName: "timestamp", width: 200 },
  context: { columnName: "context", width: 400 },
};

const ServerContextDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return (
    <div>
      {value ? (
        <a href={value} target="__blank">
          Link here
        </a>
      ) : (
        "Not available"
      )}
    </div>
  );
};

export const ServerDiagnosticsDisplay = ({
  ordersApi = true,
  spendApi = true,
}: {
  ordersApi?: boolean;
  spendApi?: boolean;
}) => {
  const [filters] = usePixelDiagnosticsPageFilters();
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 200;

  const {
    data: tableRawData,
    loading: loadingTableData,
    refetch: refetchTableData,
  } = useNorthbeamQuery<
    FetchServerAPITrackingEvents,
    FetchServerAPITrackingEventsVariables
  >(FETCH_SERVER_API_TRACKING_EVENTS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    variables: {
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit: pageSize,
      skip: pageSize * currentPage,
      types: [
        ...(spendApi ? [ServerAPIServiceType.spend_api] : []),
        ...(ordersApi ? [ServerAPIServiceType.orders_api] : []),
      ],
    },
  });

  const previousData = usePrevious(tableRawData);

  const tableData = useMemo(() => {
    if (loadingTableData) {
      return previousData;
    }
    return tableRawData;
  }, [loadingTableData, previousData, tableRawData]);

  const { data, loading, refetch } = useNorthbeamQuery<
    FetchServerAPISummaryTrackingEvents,
    FetchServerAPISummaryTrackingEventsVariables
  >(FETCH_SERVER_API_SUMMARY, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    variables: {
      startDate: filters.startDate,
      endDate: filters.endDate,
      timeGranularity: filters.timeGranularity,

      types: [
        ...(spendApi ? [ServerAPIServiceType.spend_api] : []),
        ...(ordersApi ? [ServerAPIServiceType.orders_api] : []),
      ],
    },
  });

  const { datasets, labels } = buildServerDataset(
    filters,
    data?.me?.serverApiTrackingEvents?.summary,
    { ordersApi, spendApi },
  );

  const { rows } = useMemo(() => {
    const _rows =
      tableData?.me?.serverApiTrackingEvents?.events?.rows?.map(
        (errorEvent) => ({
          request_id: errorEvent.request_id,
          message: errorEvent.message,
          error_type: errorEvent.error_type,
          timestamp: `${moment(errorEvent.timestamp).format(
            "MMM D, YYYY hh:mm a",
          )} (${user.timezone})`,
          context: errorEvent.context,
        }),
      ) ?? [];
    return { rows: _rows };
  }, [user.timezone, tableData?.me?.serverApiTrackingEvents?.events?.rows]);

  return (
    <div className="w-full">
      <div className="flex flex-row items-center  gap-5">
        <div className="text-slate-900 text-xl font-semibold leading-loose ">
          Server API Error Tracking (errors will show up 1-3 mins after they
          occur)
        </div>
        <PrimaryButton
          className="flex font-semibold gap-2 items-center justify-center h-8 w-32"
          loading={loading || loadingTableData}
          onClick={() => {
            refetch();
            refetchTableData();
          }}
        >
          <i className="fa-solid fa-rotate-right"></i>
          <div className="font-semibold">Refetch</div>
        </PrimaryButton>
      </div>
      <div className="w-full">
        <ChartControl />
        <div className="flex justify-center gap-2 py-3 space-x-2">
          {datasets.map((dataset) => (
            <div
              className="flex items-center flex-row gap-2"
              key={dataset.label}
            >
              <div
                className="h-3 w-3"
                style={{
                  backgroundColor: dataset.backgroundColor as string,
                }}
              ></div>
              <div key={dataset.label}>{dataset.label}</div>
            </div>
          ))}
        </div>
        <div className="relative w-full h-[20rem]">
          {loading && (
            <div className="absolute-center w-36">
              <LoadingSlideNoText className="w-auto" />
            </div>
          )}
          <Bar
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: "index",
                intersect: false,
              },
              plugins: {
                legend: { display: false },
                title: {
                  display: true,
                  align: "start",
                  font: {
                    size: 18,
                  },
                },
              },
              scales: {
                xAxis: {
                  title: {
                    display: true,
                    text: `${user.timezone} Timezone`,
                  },
                  type: "time",
                  time: {
                    tooltipFormat: `ddd D MMM ${
                      filters.timeGranularity === "HOURLY" ? "h:mma" : ""
                    }`,
                    unit: granularityToUnit(filters.timeGranularity),
                  },
                  ticks: {
                    padding: 10,
                    maxRotation: 0,
                    autoSkip: true,
                    font: {
                      family: "Commons Pro",
                      size: 13,
                    },
                    ...(filters.timeGranularity ===
                      TimeGranularityEnum.HOURLY && {
                      major: {
                        enabled: true,
                      },
                    }),
                  },
                  grid: {
                    drawOnChartArea: false,
                    drawTicks: false,
                  },
                },
                totalY: {
                  grid: {
                    drawOnChartArea: true,
                    drawTicks: false,
                    drawBorder: false,
                  },
                  type: "linear",
                  position: "left",
                  ticks: {
                    font: {
                      family: "Commons Pro Mono",
                      size: 11,
                    },
                  },
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Error events",
                  },
                },
              },
              layout: {
                padding: {
                  bottom: 36,
                },
              },
            }}
            data={{
              labels,
              datasets: datasets as any,
            }}
          />
        </div>
      </div>
      <div className="w-full">
        <div className="text-slate-900 text-xl font-semibold mb-5 ">
          Server API Errors
        </div>
        <ServerDiagnosticsTable
          rows={rows}
          columnMapping={SERVER_COLUMN_MAPPINGS}
          isLoading={loadingTableData}
          defaultColumnWidths={SERVER_DEFAULT_COLUMN_WIDTHS}
          totalCount={
            tableData?.me.serverApiTrackingEvents?.events?.totalCount ?? 0
          }
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          pageSize={pageSize}
        >
          <DataTypeProvider
            for={["context"]}
            formatterComponent={ServerContextDataFormatter}
          />
          <DataTypeProvider
            for={["request_id"]}
            formatterComponent={RawDataDataFormatter}
          />
          <DataTypeProvider
            for={["message"]}
            formatterComponent={PixelRawDataDataFormatter}
          />
        </ServerDiagnosticsTable>
      </div>
    </div>
  );
};

function ServerToolbar({ totalCount }: { totalCount: number }) {
  return (
    <div className="h-14  bg-[#f8f8f8] text-right flex items-center justify-end pr-5 text-lg border-b">
      <span className="mr-1">Total Errors:</span>{" "}
      <span>
        {formatting.formatInteger(totalCount, { precision: "exact" })}
      </span>
    </div>
  );
}
export function ServerDiagnosticsTable<T extends object>({
  rows,
  columnMapping,
  isLoading,
  defaultColumnWidths,
  totalCount,
  setCurrentPage,
  currentPage,
  pageSize,
  children,
}: PropsWithChildren<{
  rows: T[];
  columnMapping: { [K in keyof T]: Column };
  defaultColumnWidths: {
    [K in keyof T]: { columnName: string; width: number };
  };
  isLoading: boolean;
  totalCount: number;
  setCurrentPage: (currentPage: number) => void;
  currentPage: number;
  pageSize: number;
}>) {
  return (
    <div className="text-sm overflow-y-auto border rounded-md relative diagnositics-table">
      <Grid columns={Object.values(columnMapping)} rows={rows}>
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={setCurrentPage}
          pageSize={pageSize}
        />
        <CustomPaging totalCount={totalCount} />
        <Table />
        <TableColumnResizing
          defaultColumnWidths={Object.values(defaultColumnWidths)}
        />
        <TableHeaderRow
          contentComponent={TableHeaderRowContent}
          sortLabelComponent={TableHeaderRowSortLabel}
        />
        <Toolbar
          rootComponent={() => <ServerToolbar totalCount={totalCount} />}
        />
        <PagingPanel />
        <LoadingPanel isLoading={isLoading} loadingClassname="w-32 " />
        {/* Custom Data Formatter */}
        {children}
      </Grid>
    </div>
  );
}

export function DiagnosticsTable<T extends object>({
  rows,
  columnMapping,
  isLoading,
  defaultColumnWidths,
  children,
}: PropsWithChildren<{
  rows: T[];
  columnMapping: { [K in keyof T]: Column };
  defaultColumnWidths: {
    [K in keyof T]: { columnName: string; width: number };
  };
  isLoading: boolean;
}>) {
  return (
    <div className="text-sm  border rounded-md relative diagnositics-table">
      <Grid columns={Object.values(columnMapping)} rows={rows}>
        <SearchState />
        <PagingState defaultCurrentPage={0} pageSize={8} />
        <IntegratedFiltering />
        <IntegratedPaging />
        <Table />
        <TableColumnResizing
          defaultColumnWidths={Object.values(defaultColumnWidths)}
        />
        <TableHeaderRow
          contentComponent={TableHeaderRowContent}
          sortLabelComponent={TableHeaderRowSortLabel}
        />
        <Toolbar />
        <SearchPanel />
        <PagingPanel />
        <LoadingPanel isLoading={isLoading} loadingClassname="w-32" />
        {/* Custom Data Formatter */}
        {children}
      </Grid>
    </div>
  );
}

const buildServerDataset = (
  filters: FetchServerAPISummaryTrackingEventsVariables,
  graphData?: FetchServerAPISummaryTrackingEvents_me_serverApiTrackingEvents_summary[],
  options?: { spendApi: boolean; ordersApi: boolean },
): ChartData<"line", number[]> => {
  const dateLabels: ChartData<"line", ScatterDataPoint[], string>["labels"] =
    [];
  const ordersApiDataset: ChartDataset<"line", number[]>["data"] = [];
  const spendApiDataset: ChartDataset<"line", number[]>["data"] = [];

  graphData?.forEach(({ values, datetime }) => {
    dateLabels.push(datetime);
    ordersApiDataset.push(values.byType.ordersApi.count);
    spendApiDataset.push(values.byType.spendApi.count);
  });

  const startDate = moment(filters.startDate).startOf("day");
  if (moment(dateLabels[0]).isAfter(startDate)) {
    dateLabels.unshift(startDate.toISOString());
    ordersApiDataset.unshift(0);
    spendApiDataset.unshift(0);
  }
  const endDate = moment(filters.endDate).startOf("day");
  if (moment(dateLabels[dateLabels.length - 1]).isBefore(endDate, "hour")) {
    dateLabels.push(endDate.toISOString());
    ordersApiDataset.push(0);
    spendApiDataset.push(0);
  }

  const datasets = [];
  if (options?.spendApi) {
    datasets.push({
      label: "Spend API Errors",
      data: spendApiDataset,
      backgroundColor: "#E2230A",
      borderColor: "#E2230A",

      yAxisID: "totalY",
    });
  }
  if (options?.ordersApi) {
    datasets.push({
      label: "Orders API Errors",
      data: ordersApiDataset,
      backgroundColor: "#E2230A",
      borderColor: "#E2230A",
      yAxisID: "totalY",
    });
  }

  return {
    labels: dateLabels,
    datasets,
  };
};

const PIXEL_COLUMN_MAPPINGS = {
  id: { name: "id", title: "ID" },
  action: { name: "action", title: "Action" },
  timestamp: { name: "timestamp", title: "Timestamp" },
  rawData: { name: "rawData", title: "Event Payload & Errors" },
  ipAddress: { name: "ipAddress", title: "IP Address" },
  geoCountry: { name: "geoCountry", title: "Country" },
  urlHost: { name: "urlHost", title: "URL Host" },
  urlPath: { name: "urlPath", title: "URL Path" },
  browser: { name: "browser", title: "Browser" },
  operatingSystem: { name: "operatingSystem", title: "Operating System" },
};

const PIXEL_DEFAULT_COLUMN_WIDTHS = {
  id: { columnName: "id", width: 200 },
  action: { columnName: "action", width: 200 },
  timestamp: { columnName: "timestamp", width: 200 },

  rawData: { columnName: "rawData", width: 350 },

  ipAddress: { columnName: "ipAddress", width: 200 },
  geoCountry: { columnName: "geoCountry", width: 200 },
  urlHost: { columnName: "urlHost", width: 200 },
  urlPath: { columnName: "urlPath", width: 200 },
  browser: { columnName: "browser", width: 200 },
  operatingSystem: { columnName: "operatingSystem", width: 200 },
};

const RawDataDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const { addToast } = useToasts();

  return (
    <div>
      {value ? (
        <div className="relative">
          <GenericTooltip
            content="Copy"
            noInfoCircle
            overlayClassName="pointer-events-none"
            className=" mr-2 absolute right-0 z-1"
          >
            <i
              className={clsx(
                "bg-white elevation-1 rounded-full p-[5px] fa-sharp fa-solid fa-clone",
                "cursor-pointer text-md text-primary font-light",
              )}
              onClick={() => {
                addToast("COPIED TO CLIPBOARD", {
                  appearance: "success",
                });
                copy(value);
              }}
            ></i>
          </GenericTooltip>
          <div className="relative overflow-x-auto  ">{value}</div>
        </div>
      ) : (
        "Not available"
      )}
    </div>
  );
};

const PixelRawDataDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const { addToast } = useToasts();
  const [isOpen, setIsOpen] = useState(false);
  const onClose = useCallback(() => setIsOpen(false), []);

  const v = useMemo(() => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return { error: value };
    }
  }, [value]);

  return (
    <div>
      {value ? (
        <div className="relative">
          <GenericTooltip
            content="See full payload and errors"
            noInfoCircle
            overlayClassName="pointer-events-none"
            className=" mr-2 absolute right-8 z-1"
          >
            <i
              className={clsx(
                "bg-white elevation-1 rounded-full p-[5px] fa-sharp fa-solid fa-arrow-up-right-from-square",
                "cursor-pointer text-md text-primary font-light",
              )}
              onClick={() => {
                setIsOpen(true);
              }}
            ></i>
          </GenericTooltip>
          <GenericTooltip
            content="Copy"
            noInfoCircle
            overlayClassName="pointer-events-none"
            className=" mr-2 absolute right-0 z-1"
          >
            <i
              className={clsx(
                "bg-white elevation-1 rounded-full p-[5px] fa-sharp fa-solid fa-clone",
                "cursor-pointer text-md text-primary font-light",
              )}
              onClick={() => {
                addToast("COPIED TO CLIPBOARD", {
                  appearance: "success",
                });
                copy(value);
              }}
            ></i>
          </GenericTooltip>
          <div className="relative overflow-x-auto  ">{value}</div>
        </div>
      ) : (
        "Not available"
      )}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
        bodyClassName="px-0 py-0"
        size="xl"
      >
        <JsonEditor
          value={v}
          mode="view"
          ace={ace}
          htmlElementProps={{ style: { height: 600 } }}
        />
      </Modal>
    </div>
  );
};

const StringArrayParam = CustomJsonParam<string[]>();

export const PixelDiagnosticsChart = () => {
  const [filters] = usePixelDiagnosticsPageFilters();
  const { user } = useUser();
  const [hiddenSelectedPixelLegend, setHiddenPixelLegend] = useQueryParam(
    "pixel-diagnostics-legend",
    withDefault(StringArrayParam, [] as string[]),
  );

  const hiddenSelectedPixelLegendSet = useMemo(() => {
    return new Set(hiddenSelectedPixelLegend);
  }, [hiddenSelectedPixelLegend]);

  const { data, loading, refetch } = useNorthbeamQuery<
    FetchPixelTrackingEvents,
    FetchPixelTrackingEventsVariables
  >(FETCH_PIXEL_TRACKING_EVENTS, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    variables: {
      startDate: filters.startDate,
      endDate: filters.endDate,
      timeGranularity: filters.timeGranularity,
    },
  });

  const { datasets, labels } = buildDataset(
    filters,
    data?.me?.pixelTrackingEvents?.summary,
  );

  const pageViewsDataset = useMemo(() => {
    return datasets.find((i) => i.label === "Page views")!;
  }, [datasets]);

  const otherPixelDatasets = useMemo(() => {
    return datasets
      .filter((i) => i.label !== "Page views")
      .map((d) =>
        hiddenSelectedPixelLegendSet.has(d.label ?? "")
          ? { ...d, hidden: true }
          : { ...d, hidden: false },
      );
  }, [datasets, hiddenSelectedPixelLegendSet]);

  const { rows } = useMemo(() => {
    const _rows =
      data?.me?.pixelTrackingEvents?.fpeErrorEvents?.map((errorEvent) => {
        const { browser, device, os } = errorEvent.userAgent
          ? UAParser(errorEvent.userAgent)
          : {};

        const url = errorEvent.pageURL ? new URL(errorEvent.pageURL) : null;
        return {
          id: errorEvent.id,
          action: errorEvent.action,
          timestamp: `${moment(errorEvent.timestamp).format(
            "MMM D, YYYY hh:mm a",
          )} (${user.timezone})`,

          rawData: errorEvent.rawData,
          ipAddress: errorEvent.userIPAddress,
          geoCountry: errorEvent.geoCountry,
          urlHost: url?.protocol + "//" + url?.host,
          urlPath: `${url?.pathname}${url?.search}`,
          browser: `${browser?.toString()}`,
          operatingSystem: `${os?.name}${
            device?.model ? ` - ${device.model}` : ""
          }`,
        };
      }) ?? [];
    return { rows: _rows };
  }, [data?.me?.pixelTrackingEvents?.fpeErrorEvents, user.timezone]);

  return (
    <div>
      <div className="flex flex-row items-center gap-5">
        <div className="text-slate-900 text-xl font-semibold leading-loose ">
          Pixel Tracking (events will show up 1-5 mins after they occur)
        </div>
        <PrimaryButton
          className="flex font-semibold gap-2 items-center justify-center h-8 w-32"
          loading={loading}
          onClick={async () => {
            await refetch();
          }}
        >
          <i className="fa-solid fa-rotate-right"></i>
          <div className="font-semibold">Refetch</div>
        </PrimaryButton>
      </div>
      <ChartControl />
      <div className="flex justify-center gap-2 py-3 space-x-2">
        {otherPixelDatasets.map((dataset) => {
          const datasetLabel = dataset.label ?? "";
          return (
            <div
              className={clsx(
                "flex items-center flex-row gap-2 cursor-pointer ",
                {
                  "line-through":
                    hiddenSelectedPixelLegendSet.has(datasetLabel),
                },
              )}
              key={datasetLabel}
              onClick={() => {
                if (hiddenSelectedPixelLegendSet.has(datasetLabel)) {
                  setHiddenPixelLegend(
                    hiddenSelectedPixelLegend.filter((i) => i !== datasetLabel),
                  );
                } else {
                  setHiddenPixelLegend([
                    ...hiddenSelectedPixelLegend,
                    datasetLabel,
                  ]);
                }
              }}
            >
              <div
                className="h-3 w-3"
                style={{
                  backgroundColor: dataset.borderColor as string,
                }}
              ></div>
              <div key={dataset.label}>{dataset.label}</div>
            </div>
          );
        })}
      </div>
      <div className="relative m-auto w-[99%] h-[20rem]">
        {loading && (
          <div className="absolute-center w-36">
            <LoadingSlideNoText className="w-auto" />
          </div>
        )}
        <Bar
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "index",
              intersect: false,
            },
            plugins: {
              legend: { display: false },
            },
            scales: {
              xAxis: {
                title: {
                  display: true,
                  text: `${user.timezone} Timezone`,
                },
                stacked: true,
                type: "time",
                time: {
                  tooltipFormat: `ddd D MMM ${
                    filters.timeGranularity === "HOURLY" ? "h:mma" : ""
                  }`,
                  unit: granularityToUnit(filters.timeGranularity),
                },
                ticks: {
                  padding: 10,
                  maxRotation: 0,
                  autoSkip: true,
                  font: {
                    family: "Commons Pro",
                    size: 13,
                  },
                  ...(filters.timeGranularity ===
                    TimeGranularityEnum.HOURLY && {
                    major: {
                      enabled: true,
                    },
                  }),
                },
                grid: {
                  drawOnChartArea: false,
                  drawTicks: false,
                },
              },
              totalY: {
                stacked: true,
                grid: {
                  drawOnChartArea: true,
                  drawTicks: false,
                  drawBorder: false,
                },
                type: "linear",
                position: "left",
                ticks: {
                  font: {
                    family: "Commons Pro Mono",
                    size: 11,
                  },
                },
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Purchase Events and Custom Goals",
                },
              },
            },
            layout: {
              padding: {
                bottom: 36,
              },
            },
          }}
          data={{
            labels,
            datasets: otherPixelDatasets as any,
          }}
        />
      </div>
      <div className="flex justify-center gap-2 py-3 space-x-2">
        <div
          className={clsx("flex items-center flex-row gap-2")}
          key={pageViewsDataset.label}
        >
          <div
            className="h-3 w-3"
            style={{
              backgroundColor: pageViewsDataset.borderColor as string,
            }}
          ></div>
          <div key={pageViewsDataset.label}>{pageViewsDataset.label}</div>
        </div>
      </div>
      <div className="relative m-auto w-[99%] h-[20rem]">
        {loading && (
          <div className="absolute-center w-36">
            <LoadingSlideNoText className="w-auto" />
          </div>
        )}
        <Line
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "index",
              intersect: false,
            },
            plugins: {
              legend: { display: false },
            },
            scales: {
              xAxis: {
                stacked: true,
                type: "time",
                title: {
                  display: true,
                  text: `${user.timezone} Timezone`,
                },
                time: {
                  tooltipFormat: `ddd D MMM ${
                    filters.timeGranularity === "HOURLY" ? "h:mma" : ""
                  }`,
                  unit: granularityToUnit(filters.timeGranularity),
                },
                ticks: {
                  padding: 10,
                  maxRotation: 0,
                  autoSkip: true,
                  font: {
                    family: "Commons Pro",
                    size: 13,
                  },
                  ...(filters.timeGranularity ===
                    TimeGranularityEnum.HOURLY && {
                    major: {
                      enabled: true,
                    },
                  }),
                },
                grid: {
                  drawOnChartArea: false,
                  drawTicks: false,
                },
              },
              totalY: {
                stacked: true,
                grid: {
                  drawOnChartArea: true,
                  drawTicks: false,
                  drawBorder: false,
                },
                type: "linear",
                position: "left",
                ticks: {
                  font: {
                    family: "Commons Pro Mono",
                    size: 11,
                  },
                },
                beginAtZero: true,
                title: {
                  display: true,
                  text: "Pageviews",
                },
              },
            },
            layout: {
              padding: {
                bottom: 36,
              },
            },
          }}
          data={{
            labels,
            datasets: [pageViewsDataset as any],
          }}
        />
      </div>
      <div className="text-slate-900 text-xl font-semibold mb-5 ">
        Purchase Event Errors
      </div>
      <DiagnosticsTable
        isLoading={loading}
        rows={rows}
        defaultColumnWidths={PIXEL_DEFAULT_COLUMN_WIDTHS}
        columnMapping={PIXEL_COLUMN_MAPPINGS}
      >
        <DataTypeProvider
          for={["rawData"]}
          formatterComponent={PixelRawDataDataFormatter}
        />
      </DiagnosticsTable>
    </div>
  );
};

const buildDataset = (
  filters: FetchPixelTrackingEventsVariables,
  graphData?: FetchPixelTrackingEvents_me_pixelTrackingEvents_summary[],
): ChartData => {
  const dateLabels: ChartData<"line", ScatterDataPoint[], string>["labels"] =
    [];
  const pageViewsDataSet: ChartDataset<"line", number[]>["data"] = [];
  const identifyDataSet: ChartDataset<"line", number[]>["data"] = [];
  const customGoalsDataSet: ChartDataset<"line", number[]>["data"] = [];
  const purchaseEventsDataSet: ChartDataset<"line", number[]>["data"] = [];
  const purchaseEventErrors: ChartDataset<"line", number[]>["data"] = [];

  graphData?.forEach(({ values, datetime }) => {
    dateLabels.push(datetime);
    pageViewsDataSet.push(values.byType.pageView.count);
    identifyDataSet.push(values.byType.identify.count);
    customGoalsDataSet.push(values.byType.customGoal.count);
    purchaseEventsDataSet.push(values.byType.purchaseEvent.count);
    purchaseEventErrors.push(values.byType.purchaseEventError.count);
  });

  const startDate = moment(filters.startDate).startOf("day");
  if (moment(dateLabels[0]).isAfter(startDate)) {
    dateLabels.unshift(startDate.toISOString());
    pageViewsDataSet.unshift(0);
    identifyDataSet.unshift(0);
    customGoalsDataSet.unshift(0);
    purchaseEventsDataSet.unshift(0);
    purchaseEventErrors.unshift(0);
  }
  const endDate = moment(filters.endDate).startOf("day");
  if (moment(dateLabels[dateLabels.length - 1]).isBefore(endDate, "hour")) {
    dateLabels.push(endDate.toISOString());
    pageViewsDataSet.push(0);
    identifyDataSet.push(0);
    customGoalsDataSet.push(0);
    purchaseEventsDataSet.push(0);
    purchaseEventErrors.push(0);
  }

  const datasets = [
    {
      label: "Page views",
      data: pageViewsDataSet,
      pointRadius: 3,
      backgroundColor: "#7081FF",
      borderColor: "#7081FF",
      yAxisID: "totalY",
    },
    {
      label: "Purchase Event Errors",
      data: purchaseEventErrors,
      pointRadius: 3,
      backgroundColor: "#E2230A",
      borderColor: "#E2230A",
      yAxisID: "totalY",
    },
    {
      label: "Successful Purchase Events",
      data: purchaseEventsDataSet,
      pointRadius: 3,
      backgroundColor: "#5CBF64",
      borderColor: "#5CBF64",
      yAxisID: "totalY",
    },
  ];

  const hasIdentifyCalls = identifyDataSet.filter(Boolean).length > 0;
  const hasCustomGoals = customGoalsDataSet.filter(Boolean).length > 0;

  if (hasIdentifyCalls) {
    datasets.push({
      label: "Identify",
      data: identifyDataSet,
      pointRadius: 0,
      backgroundColor: "#FFD247",
      borderColor: "#FFD247",
      yAxisID: "totalY",
    });
  }

  if (hasCustomGoals) {
    datasets.push({
      label: "Custom goals",
      data: customGoalsDataSet,
      pointRadius: 0,
      backgroundColor: "#71D5D5",
      borderColor: "#71D5D5",
      yAxisID: "totalY",
    });
  }
  return {
    labels: dateLabels,
    datasets,
  };
};

function granularityToUnit(granularity: TimeGranularityEnum): TimeUnit {
  switch (granularity) {
    case TimeGranularityEnum.DAILY:
      return "day";
    case TimeGranularityEnum.HOURLY:
      return "hour";
    case TimeGranularityEnum.WEEKLY:
      return "week";
  }
}
